import {
  decodeSecretReference,
  encodeSecretReference,
} from "@playbookai/integration-kernel";
import { aisRequest, type ModelConnection } from "./admin-genai-api.js";
import {
  CONTROL_OR_SURROGATE,
  countCodePointsBounded,
} from "./tenant-config-api.js";

const BASE = "/api/ais/v1/admin";
export interface ConfigField {
  title?: string;
  type?: string;
  enum?: string[];
  default?: string;
  description?: string;
  /* Pydantic emits these into model_json_schema(), so AIS already publishes
     them per field - Vertex's project is 6-63 characters matching
     ^[a-z][a-z0-9-]+[a-z0-9]$, Bedrock's region has its own pattern. Declaring
     them here is what stops the form dropping constraints it was handed. */
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  anyOf?: ConfigField[];
}
export interface ProviderDescriptor {
  id: string;
  label: string;
  auth_modes: string[];
  modes: string[];
  endpoint_required: boolean;
  api_version_required: boolean;
  /* The closed sets AIS enforces, served per provider so this client keeps no
     copy of a server rule. Empty does NOT mean unrestricted in either case.
     For the endpoint lists, hosts and suffixes are ONE allowlist and AIS
     refuses any endpoint when a provider declares neither. For api_versions,
     empty means the provider publishes no choice, and whether a version may
     be sent at all is decided separately - `openai` refuses a non-null one
     outright. Same wording as the ais.yaml descriptions. */
  api_versions: string[];
  endpoint_hosts: string[];
  endpoint_suffixes: string[];
  endpoint_paths: string[];
  /* True when the provider accepts an optional endpoint on any https host;
     the host allowlist then does not apply, every other rule still does. */
  endpoint_any_host?: boolean;
  websocket_requirements?: {
    mode: "chat";
    protocol: string;
    api_version?: string | null;
  } | null;
  configuration_schema: {
    properties: Record<string, ConfigField>;
    required?: string[];
  };
}
export interface AllowedAction {
  allowed: boolean;
  reason?: string | null;
}
export interface LifecycleFields {
  locked: boolean;
  lifecycle: string;
  allowed_actions: Record<string, AllowedAction>;
  qualification: { status: string; current: boolean };
}
export interface AdminConnection
  extends LifecycleFields, Omit<ModelConnection, "provider_key"> {
  provider_key: string;
  profile_count: number;
  description?: string;
  version: number;
  provider_config: Record<string, string>;
  credential_binding: Record<string, string>;
  test_results: {
    checks?: Record<string, string>;
    transports?: Record<string, ConnectionTestResult>;
  };
}
export interface ProfileSettings {
  max_input_tokens: number;
  max_output_tokens: number;
  reasoning_effort: string | null;
  temperature: number | null;
  top_p: number | null;
}

/**
 * Conversation-context policy — a THIRD edit boundary beside execution
 * settings and metadata.
 *
 * Editable at any lifecycle state, including on a LOCKED profile: it changes
 * neither what the model is asked nor how it is reached, so it is excluded
 * from the execution fingerprint and cannot invalidate a recorded
 * configuration test. Optional on the READ type only because a profile stored
 * before the column existed carries none; AIS defaults it on read.
 */
export interface ProfileContextPolicy {
  auto_compaction_enabled: boolean;
  compaction_trigger_percent: number;
  compaction_target_percent: number;
}
export interface ExecutionProfile extends LifecycleFields {
  connection?: {
    id?: string;
    display_name?: string;
    provider_key?: string;
    model_display_name?: string | null;
  };
  catalogue_model_id: string;
  model_or_deployment: string | null;
  latest_test?: { id?: string; state: string; message?: string | null };
  id: string;
  display_name: string;
  description: string;
  connection_id: string;
  settings: ProfileSettings;
  context_policy?: ProfileContextPolicy;
  enabled: boolean;
  available: boolean;
  version: number;
  availability_generation: number;
}
export interface ModelDefaults {
  platform_profile_id: string | null;
  apps: Record<string, string | null>;
  version: number;
}
export interface ProfileDependencies {
  platform_default: boolean;
  app_defaults: string[];
}
export type ConnectionDependencies = {
  profiles: ({ id: string; display_name: string } & ProfileDependencies)[];
};
export type ProfileInput = Pick<
  ExecutionProfile,
  | "display_name"
  | "description"
  | "connection_id"
  | "settings"
  | "catalogue_model_id"
  | "model_or_deployment"
> & {
  /**
   * OPTIONAL on the wire, and sent on every save the editor makes — including
   * a LOCKED profile's, which is the whole point of it being its own boundary.
   * `saveProfile` therefore does NOT strip it when it strips the execution
   * fields.
   *
   * Optional rather than required because that is what the server means by
   * absence, and the type should not claim otherwise: `CreateProfileRequest`
   * applies the shipped 80/20 default when it is missing, and
   * `UpdateProfileRequest` documents omission as "leave the stored policy
   * alone". Requiring it here would also force every unrelated caller to
   * invent a policy just to satisfy the type.
   */
  context_policy?: ProfileContextPolicy;
};
/* Field rules, mirroring the AIS create path in
   services/ais/ais/model_connections.py in the same order and with the same
   counting, so the form never blocks a value the server would take (or waves
   through one it would reject):
     - every rule runs on the TRIMMED value, because the trim is what the
       editors put on the wire. Measuring the raw input instead rejects a name
       followed by spaces that the payload would not have carried;
     - bounds count CODE POINTS. String.length counts UTF-16 units, which
       rejects a 160-emoji name the contract and the column both accept - the
       same reason tenant-config-api.ts counts this way, and the reason no
       field here carries a maxLength attribute;
     - the closed sets are read from the provider descriptor, so there is no
       copy in this client that could drift from what AIS enforces. */
export const NAME_MAX = 160;
export const DESCRIPTION_MAX = 1000;
export const SECRET_REFERENCE_MAX = 2048;
export const ENDPOINT_URL_MAX = 2048;
export const API_VERSION_MAX = 128;

function validateText(
  value: string,
  label: string,
  max: number,
  required: boolean,
): string | null {
  const trimmed = value.trim();
  if (countCodePointsBounded(trimmed, max) > max) {
    return `${label} must be ${max} characters or fewer.`;
  }
  if (trimmed.length === 0) return required ? `${label} is required.` : null;
  if (CONTROL_OR_SURROGATE.test(trimmed)) {
    return `${label} must not contain control characters.`;
  }
  return null;
}

export const validateConnectionName = (value: string): string | null =>
  validateText(value, "Connection name", NAME_MAX, true);
export const validateProfileName = (value: string): string | null =>
  validateText(value, "Profile name", NAME_MAX, true);
export const validateDescription = (value: string): string | null =>
  validateText(value, "Description", DESCRIPTION_MAX, false);

/* A workload identity binding is not a Key Vault URI - AIS stores it in
   identity_binding_id and refuses a secret_uri alongside it - so the field
   shares a control with the secret reference but not its shape rule. */
export const IDENTITY_BINDING_MAX = 128;
export const validateIdentityBinding = (value: string): string | null =>
  validateText(value, "Operator identity binding", IDENTITY_BINDING_MAX, true);

/* Mirrors AIS normalize_secret_reference using the shared envelope contract.
   The secret store is deployment-wide, NOT selected by the model provider.
   AIS still enforces the deployment provider and store boundary. Preserve the
   submitted reference (especially a pinned version); this checks syntax only. */
export function validateSecretReference(value: string): string | null {
  const trimmed = value.trim();
  if (
    countCodePointsBounded(trimmed, SECRET_REFERENCE_MAX) > SECRET_REFERENCE_MAX
  ) {
    return `Secret reference must be ${SECRET_REFERENCE_MAX} characters or fewer.`;
  }
  if (trimmed.length === 0) return "A secret reference is required.";
  try {
    encodeSecretReference(
      trimmed.startsWith("{")
        ? decodeSecretReference(trimmed)
        : {
            schemaVersion: 1,
            provider: trimmed.startsWith("arn:")
              ? "aws-secrets-manager"
              : "azure-key-vault",
            mode: "latest",
            locator: trimmed,
          },
    );
    return null;
  } catch {
    // Never echo parser errors or input: a mistaken paste may contain a secret.
    if (trimmed.startsWith("{")) {
      return "Enter a valid secret-reference JSON envelope (schemaVersion 1). Pinned references require a version; latest references must omit it. The encoded reference must fit within 500 characters.";
    }
    if (trimmed.startsWith("arn:")) {
      return "Enter a full AWS Secrets Manager secret ARN with region and 12-digit account ID (320 characters or fewer).";
    }
  }
  // Specific guidance for common Azure portal paste mistakes, after the shared
  // contract has refused the reference. These diagnostics never accept input.
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return "Enter a versionless Azure Key Vault URI, a full AWS Secrets Manager secret ARN, or a secret-reference JSON envelope. Do not enter the secret value.";
  }
  if (url.protocol !== "https:") return "The secret reference must use https.";
  if (url.search || url.hash) {
    return "The secret reference must not include a query or fragment.";
  }
  if (url.pathname.includes(";")) {
    return "The secret reference must not include parameters.";
  }
  // Inspect raw authority: URL lowercases hosts and hides the default port.
  const authority =
    trimmed.slice(trimmed.indexOf("://") + 3).split("/")[0] ?? "";
  if (!authority.endsWith(".vault.azure.net")) {
    const host = authority.split("@").pop() ?? authority;
    if (/:\d+$/.test(host)) {
      return "The secret reference must not include a port, not even the default 443.";
    }
    if (host.toLowerCase().endsWith(".vault.azure.net")) {
      return "The secret reference host must be lower case.";
    }
    return "The secret reference must point to an Azure Key Vault host.";
  }
  const parts = url.pathname.split("/").filter(Boolean);
  if (parts[0] === "secrets" && parts.length > 2) {
    return "Remove the version segment from the end - use .../secrets/{name}.";
  }
  if (parts.length !== 2 || parts[0] !== "secrets" || !parts[1]) {
    return "The secret reference must be .../secrets/{name}.";
  }
  return "Enter a valid versionless Azure Key Vault secret URI (320 characters or fewer, with no embedded credentials).";
}

/* Mirrors validate_connection_configuration. Note the host test has no
   unrestricted case: AIS refuses any endpoint for a provider that declares
   neither a host nor a suffix, so treating empty lists as "anything goes"
   would wave through a refusal. */
export function validateEndpointUrl(
  value: string,
  provider: ProviderDescriptor | undefined,
): string | null {
  const trimmed = value.trim();
  if (countCodePointsBounded(trimmed, ENDPOINT_URL_MAX) > ENDPOINT_URL_MAX) {
    return `Provider endpoint must be ${ENDPOINT_URL_MAX} characters or fewer.`;
  }
  if (trimmed.length === 0) {
    return provider?.endpoint_required
      ? "A provider endpoint is required."
      : null;
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return "Enter a full https endpoint URL.";
  }
  if (url.protocol !== "https:") return "The endpoint must use https.";
  if (url.username || url.password) {
    return "The endpoint must not include a username or password.";
  }
  if (url.search || url.hash) {
    return "The endpoint must not include a query or fragment.";
  }
  if (url.port !== "" && url.port !== "443") {
    return "The endpoint must use the default https port.";
  }
  /* Compared as it comes: validate_connection_configuration reads
     `parsed.hostname` without stripping a trailing dot, so normalising here
     would accept `x.openai.azure.com.` that AIS refuses. */
  const host = url.hostname;
  const hosts = provider?.endpoint_hosts;
  const suffixes = provider?.endpoint_suffixes;
  /* Absent is not empty. An AIS that predates these fields publishes neither,
     and the server is then the only judge of the host - inventing a verdict
     here would disable the form. Empty-but-present is the server's own "no
     endpoint accepted", so that IS mirrored. */
  if (
    !provider?.endpoint_any_host &&
    hosts !== undefined &&
    suffixes !== undefined &&
    !hosts.includes(host) &&
    !suffixes.some((s) => host.endsWith(s))
  ) {
    const expected = [
      ...hosts,
      ...suffixes.map((s) => `a host ending in ${s}`),
    ];
    return expected.length > 0
      ? `This provider accepts ${expected.join(" or ")}.`
      : "This provider does not accept an endpoint URL.";
  }
  const paths = provider?.endpoint_paths;
  if (
    paths !== undefined &&
    !paths.includes(url.pathname.replace(/\/+$/, ""))
  ) {
    return paths.length === 1 && paths[0] === ""
      ? "Enter the host only, with no path."
      : `The path must be one of ${paths.map((v) => v || "none").join(", ")}.`;
  }
  return null;
}

export function validateApiVersion(
  value: string,
  provider: ProviderDescriptor | undefined,
): string | null {
  const trimmed = value.trim();
  if (countCodePointsBounded(trimmed, API_VERSION_MAX) > API_VERSION_MAX) {
    return `API version must be ${API_VERSION_MAX} characters or fewer.`;
  }
  if (trimmed.length === 0) {
    return provider?.api_version_required
      ? "An API version is required."
      : null;
  }
  const allowed = provider?.api_versions ?? [];
  if (allowed.length > 0 && !allowed.includes(trimmed)) {
    return `This provider accepts ${allowed.join(", ")}.`;
  }
  return null;
}

export const INFERENCE_TARGET_MAX = 256;

/* Every rule below replaces a native constraint that `noValidate` switched off.
   Leaving one unreplaced is worse than before the change: the value reaches AIS
   and comes back in the page-level banner this work exists to remove. */
export const validateInferenceTarget = (
  value: string,
  required: boolean,
): string | null =>
  validateText(value, "Deployment name", INFERENCE_TARGET_MAX, required);

export function validateGatewayModelId(value: string): string | null {
  return (
    validateText(value, "Gateway model ID", INFERENCE_TARGET_MAX, true) ??
    (/^[A-Za-z0-9][A-Za-z0-9._:-]*$/.test(value.trim())
      ? null
      : "Use letters, digits, dots, hyphens, underscores or colons.")
  );
}

/* AIS states no per-key cap for provider_config, so the bound here is a client
   sanity limit borrowed from DESCRIPTION_MAX rather than a mirrored server rule.
   It is deliberately generous: a cap the server does not have must not be the
   thing that refuses a working value. */
export function validateProviderConfigValue(
  value: string,
  label: string,
  required: boolean,
  field?: ConfigField,
): string | null {
  const generic = validateText(value, label, DESCRIPTION_MAX, required);
  if (generic !== null) return generic;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  // Nullable Pydantic fields carry their string constraints in anyOf.
  const stringBranch = field?.anyOf?.find((branch) => branch.type === "string");
  if (stringBranch) field = { ...field, ...stringBranch };
  /* Counted in code points for the same reason every other bound here is: the
     server counts characters, and String.length would reject an astral value
     it accepts. */
  const length = [...trimmed].length;
  if (field?.minLength !== undefined && length < field.minLength) {
    return `${label} must be at least ${field.minLength} characters.`;
  }
  if (field?.maxLength !== undefined && length > field.maxLength) {
    return `${label} must be ${field.maxLength} characters or fewer.`;
  }
  if (field?.pattern !== undefined) {
    let shape: RegExp;
    try {
      shape = new RegExp(field.pattern);
    } catch {
      /* An unanchored or exotic pattern the browser cannot compile is the
         server's to enforce; refusing here would block a value it accepts. */
      return null;
    }
    if (!shape.test(trimmed)) {
      return field.description
        ? `${label} is not in the required format: ${field.description}`
        : `${label} must match ${field.pattern}.`;
    }
  }
  return null;
}

/* Mirrors the Pydantic bounds, `minExclusive` included: top_p is `gt=0`, which
   the control's native `min={0.000001}` only approximated. */
function validateOptionalNumberInRange(
  value: string,
  label: string,
  min: number,
  max: number,
  minExclusive = false,
): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return `${label} must be a number.`;
  if (minExclusive ? parsed <= min : parsed < min) {
    return minExclusive
      ? `${label} must be greater than ${min}.`
      : `${label} must be ${min} or more.`;
  }
  if (parsed > max) return `${label} must be ${max} or less.`;
  return null;
}

/* `supported` mirrors validate_settings_against_model, which refuses a value for
   a control the offering does not verifiably support (UNSUPPORTED_SETTINGS) and
   treats unknown support as unsupported. Without this the field is the last one
   on these forms that can still reach AIS and come back in the page-level
   banner. The hint already asks for the value to be cleared; this makes the
   refusal say so on the field. */
export const validateTemperature = (
  value: string,
  supported = true,
): string | null =>
  !supported && value.trim() !== ""
    ? "Temperature is not a verified setting for this model - clear it."
    : validateOptionalNumberInRange(value, "Temperature", 0, 2);
export const validateTopP = (value: string, supported = true): string | null =>
  !supported && value.trim() !== ""
    ? "Top P is not a verified setting for this model - clear it."
    : validateOptionalNumberInRange(value, "Top P", 0, 1, true);

/* The sixth and last refusal in validate_settings_against_model, and the only
   one that spans two fields rather than sitting on one - which is why a
   per-field sweep missed it:

     max_input_tokens + max_output_tokens > model.context_window_tokens

   Reported on `maxInput` because that is the field the server itself names in
   `details.field`. Silent while either budget is individually invalid, so the
   per-field message stays the one on screen, and silent when the model
   publishes no window. Every model in today's catalogue has
   max_input + max_output <= context_window, three of them exactly equal, so the
   per-field caps already bound the sum and this cannot fire on shipped data -
   the catalogue is seed data, so that is a property of the seed, not a
   guarantee. */
export function validateCombinedBudget(
  input: string,
  output: string,
  contextWindow: number | null | undefined,
): string | null {
  if (contextWindow == null) return null;
  const a = Number(input.trim());
  const b = Number(output.trim());
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b) || a < 1 || b < 1) {
    return null;
  }
  return a + b > contextWindow
    ? `Input and output budgets together must not exceed the model's ${contextWindow} token context window.`
    : null;
}

/* The server defaults these when absent and rejects anything below 1. Kept as
   a string check because the control is a text-mode number input whose value
   is "" when cleared, which Number() would read as 0. */
export function validateTokenBudget(
  value: string,
  label: string,
  max?: number | null,
): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return `${label} is required.`;
  if (!/^\d+$/.test(trimmed)) return `${label} must be a whole number.`;
  /* A digit string can exceed Number.MAX_SAFE_INTEGER, where Number() rounds
     before JSON.stringify and a different value than the admin typed goes on
     the wire. */
  if (!Number.isSafeInteger(Number(trimmed))) {
    return `${label} is larger than this field can carry.`;
  }
  if (Number(trimmed) < 1) return `${label} must be at least 1.`;
  /* The model ceiling was carried by the control's native max, which
     noValidate now suppresses - without this the protection would be lost. */
  if (max != null && Number(trimmed) > max) {
    return `${label} cannot exceed ${max} for the selected model.`;
  }
  return null;
}

/* Mirrors `ais.profile_types.ProfileSettings`. Kept here beside the other
   validators so the form can report the refusal on the field rather than in the
   page-level banner after a round trip. */
export const COMPACTION_TRIGGER_MIN_PERCENT = 10;
export const COMPACTION_TRIGGER_MAX_PERCENT = 95;
export const COMPACTION_TARGET_MIN_PERCENT = 5;
export const COMPACTION_TARGET_MAX_PERCENT = 90;
export const DEFAULT_COMPACTION_TRIGGER_PERCENT = 80;
export const DEFAULT_COMPACTION_TARGET_PERCENT = 20;

/**
 * The cross-field rule: the target must be STRICTLY below the trigger.
 *
 * Equality is refused as well as inversion, and for a concrete reason —
 * compacting down to the level that triggered compaction reclaims nothing and
 * would trigger again on the very next turn.
 */
export function validateCompactionThresholds(
  trigger: number,
  target: number,
): string | null {
  if (
    !Number.isInteger(trigger) ||
    trigger < COMPACTION_TRIGGER_MIN_PERCENT ||
    trigger > COMPACTION_TRIGGER_MAX_PERCENT
  ) {
    return `Compact at must be between ${COMPACTION_TRIGGER_MIN_PERCENT}% and ${COMPACTION_TRIGGER_MAX_PERCENT}%.`;
  }
  if (
    !Number.isInteger(target) ||
    target < COMPACTION_TARGET_MIN_PERCENT ||
    target > COMPACTION_TARGET_MAX_PERCENT
  ) {
    return `Compact down to must be between ${COMPACTION_TARGET_MIN_PERCENT}% and ${COMPACTION_TARGET_MAX_PERCENT}%.`;
  }
  if (target >= trigger) {
    return "Compact down to must be below Compact at.";
  }
  return null;
}

/**
 * The token count a percentage of the usable input budget works out to.
 *
 * "Usable" is the profile's own `max_input_tokens`. The OUTPUT reservation is
 * already outside it — AIS bounds input and output separately and rejects a
 * request whose estimated INPUT exceeds `max_input_tokens` — so subtracting
 * the output budget here would show an admin a smaller number than the one the
 * runtime actually measures against.
 */
export function compactionTokens(
  maxInputTokens: number,
  percent: number,
): number | null {
  if (!Number.isSafeInteger(maxInputTokens) || maxInputTokens < 1) return null;
  return Math.round((maxInputTokens * percent) / 100);
}

export type ConnectionInput = {
  display_name: string;
  provider_key: string;
  mode: string;
  secret_uri: string;
  credential_binding: Record<string, string>;
  provider_config: Record<string, string>;
  endpoint_url: string | null;
  api_version: string | null;
};

function object<T>(value: unknown, keys: string[]): T {
  if (
    !value ||
    typeof value !== "object" ||
    keys.some((key) => !(key in value))
  ) {
    throw new Error(
      "AIS returned an invalid configuration response. Refresh and retry.",
    );
  }
  return value as T;
}
async function list<T>(path: string, key: string): Promise<T[]> {
  const result = object<Record<string, unknown>>(
    await aisRequest(`${BASE}/${path}`),
    [key],
  );
  if (!Array.isArray(result[key]))
    throw new Error("AIS returned an invalid configuration list.");
  return result[key] as T[];
}
export interface AdminCursorPage<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
  totalCount?: number;
}
export interface AdminPageOptions {
  cursor?: string;
  limit?: 20 | 25 | 50;
  includeRetired?: boolean;
  availableOnly?: boolean;
}
async function page<T>(
  path: "connections" | "profiles",
  key: "connections" | "profiles",
  options: AdminPageOptions = {},
): Promise<AdminCursorPage<T>> {
  const query = new URLSearchParams();
  query.set("limit", String(options.limit ?? 25));
  if (options.cursor) query.set("cursor", options.cursor);
  if (options.includeRetired) query.set("include_retired", "true");
  if (options.availableOnly) query.set("available_only", "true");
  const result = object<Record<string, unknown>>(
    await aisRequest(`${BASE}/${path}?${query.toString()}`),
    [key, "next_cursor", "has_more"],
  );
  if (!Array.isArray(result[key]) || typeof result.has_more !== "boolean") {
    throw new Error("AIS returned an invalid configuration page.");
  }
  if (
    result.next_cursor !== null &&
    result.next_cursor !== undefined &&
    typeof result.next_cursor !== "string"
  ) {
    throw new Error("AIS returned an invalid configuration page.");
  }
  const totalCount = result.total_count;
  if (
    totalCount !== undefined &&
    (typeof totalCount !== "number" ||
      !Number.isInteger(totalCount) ||
      totalCount < 0)
  ) {
    throw new Error("AIS returned an invalid configuration page.");
  }
  return {
    items: result[key] as T[],
    nextCursor:
      typeof result.next_cursor === "string" ? result.next_cursor : null,
    hasMore: result.has_more,
    ...(typeof totalCount === "number" ? { totalCount } : {}),
  };
}
export const listProviders = () =>
  list<ProviderDescriptor>("providers", "providers");
export const pageConnections = (options?: AdminPageOptions) =>
  page<AdminConnection>("connections", "connections", options);
export const pageProfiles = (options?: AdminPageOptions) =>
  page<ExecutionProfile>("profiles", "profiles", options);
export const getDefaults = async () =>
  object<ModelDefaults>(await aisRequest(`${BASE}/model-defaults`), [
    "platform_profile_id",
    "apps",
    "version",
  ]);
const idPath = (kind: "connections" | "profiles", id: string) =>
  `${BASE}/${kind}/${encodeURIComponent(id)}`;
export const getConnection = async (id: string) =>
  object<AdminConnection>(await aisRequest(idPath("connections", id)), [
    "id",
    "display_name",
    "version",
  ]);
export const getProfile = async (id: string) =>
  object<ExecutionProfile>(await aisRequest(idPath("profiles", id)), [
    "id",
    "display_name",
    "version",
  ]);
export const getConnectionCapabilities = async (id: string, modelId: string) =>
  object<{ reasoning_efforts: string[] }>(
    await aisRequest(
      `${idPath("connections", id)}/capabilities?catalogue_model_id=${encodeURIComponent(modelId)}`,
    ),
    ["reasoning_efforts"],
  );
export const getDependencies = async (
  kind: "connections" | "profiles",
  id: string,
) =>
  object<ProfileDependencies | ConnectionDependencies>(
    await aisRequest(`${idPath(kind, id)}/dependencies`),
    kind === "profiles" ? ["platform_default", "app_defaults"] : ["profiles"],
  );
export const createConnection = (input: ConnectionInput) =>
  aisRequest(`${BASE}/connections`, {
    method: "POST",
    body: JSON.stringify(input),
  });
export const renameConnection = (row: AdminConnection, display_name: string) =>
  aisRequest(idPath("connections", row.id), {
    method: "PATCH",
    body: JSON.stringify({
      display_name,
      description: row.description ?? "",
      expected_version: row.version,
    }),
  });
export interface ConnectionTestResult {
  status: "passed" | "failed";
  tested_at?: string;
  error_code?: string | null;
  message?: string | null;
  details?: {
    checks?: Record<string, string>;
    transports?: Record<string, ConnectionTestResult>;
  } | null;
}
export const testConnection = async (id: string) => {
  const result = object<ConnectionTestResult>(
    await aisRequest(`${idPath("connections", id)}/test`, { method: "POST" }),
    ["status"],
  );
  if (result.status !== "passed" && result.status !== "failed")
    throw new Error("AIS returned an invalid connection test result.");
  return result;
};
export const saveProfile = (
  profile: ExecutionProfile | null,
  input: ProfileInput,
) =>
  aisRequest(profile ? idPath("profiles", profile.id) : `${BASE}/profiles`, {
    method: profile ? "PUT" : "POST",
    body: JSON.stringify({
      ...input,
      ...(profile ? { expected_version: profile.version } : {}),
    }),
  });
export const saveDefaults = async (value: ModelDefaults) =>
  object<ModelDefaults>(
    await aisRequest(`${BASE}/model-defaults`, {
      method: "PUT",
      body: JSON.stringify({
        platform_profile_id: value.platform_profile_id,
        apps: value.apps,
        expected_version: value.version,
      }),
    }),
    ["platform_profile_id", "apps", "version"],
  );
export const changeLifecycle = (
  kind: "connections" | "profiles",
  row: { id: string; version: number },
  action: "enable" | "disable" | "delete",
  confirmation?: {
    impact_token: string;
    replacements: Record<string, string>;
    delete_draft_profile_ids?: string[];
  },
) =>
  aisRequest(
    `${idPath(kind, row.id)}${action === "delete" ? "" : `/${action}`}`,
    {
      method: action === "delete" ? "DELETE" : "POST",
      body: JSON.stringify({
        expected_version: row.version,
        ...confirmation,
      }),
    },
  );

export interface CatalogueModel {
  id: string;
  provider_id: string;
  runtime_provider_key: string | null;
  display_name: string;
  provider_model_id: string | null;
  selectable: boolean;
  unavailable_reason?: string | null;
  effort_options: string[];
  temperature_supported: boolean | null;
  top_p_supported: boolean | null;
  max_input_tokens: number | null;
  max_output_tokens: number | null;
  context_window_tokens: number | null;
}
export const listCatalogueModels = () =>
  list<CatalogueModel>("catalogue", "models");
export const providerFamily = (key: string) =>
  ({
    openai: "OPEN_AI",
    azure_openai: "AZURE_FOUNDRY",
    azure_anthropic: "AZURE_FOUNDRY",
    anthropic: "ANTHROPIC",
    bedrock: "AMAZON_BEDROCK",
    vertex_ai: "GOOGLE_VERTEX",
  })[key];
export interface LifecyclePreview {
  subject_version: number;
  impact_token: string;
  blocked: boolean;
  blocked_reason: string | null;
  explicit_defaults: { key: string; profile_display_name: string }[];
  candidates: { id: string; display_name: string }[];
  affected_profiles: { id: string; display_name: string }[];
  deletable_draft_profiles: { id: string; display_name: string }[];
}
export const getLifecyclePreview = async (
  kind: "connections" | "profiles",
  id: string,
  action: "disable" | "delete",
) => {
  const preview = object<LifecyclePreview>(
    await aisRequest(
      `${idPath(kind, id)}/lifecycle-preview?action=${action === "delete" ? "retire" : "disable"}`,
    ),
    [
      "impact_token",
      "subject_version",
      "blocked",
      "explicit_defaults",
      "candidates",
      "affected_profiles",
      "deletable_draft_profiles",
    ],
  );
  if (
    typeof preview.impact_token !== "string" ||
    !Number.isInteger(preview.subject_version) ||
    typeof preview.blocked !== "boolean" ||
    ![
      preview.explicit_defaults,
      preview.candidates,
      preview.affected_profiles,
      preview.deletable_draft_profiles,
    ].every(Array.isArray) ||
    !preview.explicit_defaults.every(
      (row) =>
        row &&
        typeof row.key === "string" &&
        typeof row.profile_display_name === "string",
    ) ||
    ![
      ...preview.candidates,
      ...preview.affected_profiles,
      ...preview.deletable_draft_profiles,
    ].every(
      (row) =>
        row &&
        typeof row.id === "string" &&
        typeof row.display_name === "string",
    )
  ) {
    throw new Error("Invalid configuration response.");
  }
  return preview;
};
export const updateConnection = (
  row: AdminConnection,
  input: Omit<ConnectionInput, "provider_key" | "mode">,
) =>
  aisRequest(idPath("connections", row.id), {
    method: "PATCH",
    body: JSON.stringify({
      ...input,
      description: row.description ?? "",
      expected_version: row.version,
      clear: [
        input.endpoint_url === null ? "endpoint_url" : null,
        input.api_version === null ? "api_version" : null,
      ].filter(Boolean),
    }),
  });
export const duplicateConfiguration = (
  kind: "connections" | "profiles",
  id: string,
) =>
  aisRequest(`${idPath(kind, id)}/duplicate`, { method: "POST", body: "{}" });
export interface ProfileTest {
  id: string;
  state: string;
  message?: string | null;
  error_code?: string | null;
  checks?: Record<string, string>;
  diagnostic_id?: string;
}
export const startProfileTest = async (id: string) =>
  object<{ test_id: string }>(
    await aisRequest(`${idPath("profiles", id)}/test`, { method: "POST" }),
    ["test_id"],
  );
export const getProfileTest = async (id: string) =>
  object<ProfileTest>(
    await aisRequest(`${BASE}/tests/${encodeURIComponent(id)}`),
    ["state"],
  );
